import { html } from "haunted";
import { useGlobalButtonsSection } from "./useGlobalButtonsSection";
import { useEffectWithoutCleanup, useState } from "Shared/haunted/CustomHooks";
import classNames from "classnames";
import { CacheSectionId, dynamicSettingsMap, cacheMap, DynamicSettingSectionId } from "./admin-page";

export interface Props {
	dynamicSettingsExpiresOn: string;
	dynamicSettingsRefreshedOn: string;
	IsPreview: boolean;
}

export const useAdminMenu = (props: Props) => {
	const [currentPage, setCurrentPage] = useState<CacheSectionId | DynamicSettingSectionId>(undefined);

	const globalButtonsSection = useGlobalButtonsSection({ isPreview: props.IsPreview });

	useEffectWithoutCleanup(() => {
		if (!currentPage) return;
		window.location.hash = `#${currentPage}`;
	}, [currentPage]);

	useEffectWithoutCleanup(() => {
		const hash = window.location.hash?.substring(1) ?? "";
		if ((hash && dynamicSettingsMap.has(hash as DynamicSettingSectionId)) || cacheMap.has(hash as CacheSectionId)) {
			setCurrentPage(hash as CacheSectionId | DynamicSettingSectionId);
			return;
		}

		setCurrentPage("feature-switches");
	}, []);

	const logoImageTemplate = () => html`
		<img
			class="block max-h-9 common-transition lg:max-h-[42px]"
			src="/images/header/jetsmart-logo-colored.svg"
			alt="JetSMART"
		/>
	`;

	const menuItemTemplate = ([id, label]: [DynamicSettingSectionId | CacheSectionId, string]) => html`
		<li
			class=${classNames("p-2", {
				"cursor-pointer text-n-blue": currentPage !== id,
				"hover:bg-n-blue hover:bg-opacity-20": currentPage !== id,
				"text-white bg-n-blue": currentPage === id,
			})}
			@click=${() => setCurrentPage(id)}
		>
			${label}
		</li>
	`;

	const dynamicSettingsTemplate = () => html`
		<div class="mt-10 w-full">
			<h2 class="mb-2 text-left text-base font-bold uppercase text-n-blue">Dynamic Settings</h2>
			<ul class="mb-4 mt-2 list-none text-xs leading-normal text-n-dark-gray">
				<li class="flex flex-col">
					<span class="font-bold uppercase">Expires On:</span> <span> ${props.dynamicSettingsExpiresOn}</span>
				</li>
				<li class="flex flex-col">
					<span class="font-bold uppercase">Refreshed On:</span>
					<span>${props.dynamicSettingsRefreshedOn}</span>
				</li>
			</ul>
			<ul class="flex list-none flex-col text-base">
				${Array.from(dynamicSettingsMap.entries()).map(menuItemTemplate)}
			</ul>
		</div>
	`;

	const cacheItemsTemplate = () => html`
		<div class="mt-10 w-full">
			<h2 class="mb-2 text-left text-base font-bold uppercase text-n-blue">Cache States and Invalidation</h2>
			<ul class="flex list-none flex-col text-base">
				${Array.from(cacheMap.entries()).map(menuItemTemplate)}
			</ul>
		</div>
	`;

	const htmlTemplate = () =>
		html` <menu
			class="fixed inset-y-0 left-0 flex w-64 flex-col items-center overflow-y-auto border-r border-solid border-n-dark-gray bg-n-blue bg-opacity-10 p-10"
		>
			${logoImageTemplate()} ${dynamicSettingsTemplate()} ${cacheItemsTemplate()}
			${globalButtonsSection.htmlTemplate()}</menu
		>`;

	return {
		currentPage,
		htmlTemplate,
	};
};
